import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components/macro";
import {
  Divider as MuiDivider,
  Typography as MuiTypography,
  CircularProgress,
  Box,
  Button,
  Dialog,
  AppBar,
  Toolbar,
  IconButton,
  Slide,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import { spacing } from "@material-ui/system";

const Typography = styled(MuiTypography)(spacing);
const Divider = styled(MuiDivider)(spacing);

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Main = styled.main`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Section = styled.section`
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 16px;
  background: #fff;
`;

const SectionGrowWrapper = styled(Section)`
  display: flex;
  flex: 1;
  flex-direction: column;
`;

const StyledIframe = styled.iframe`
  border: none;
  width: 100%;
  flex: 1;
  height: 100%;
  min-height: 600px;
`;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const PrfStatisticsTool = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  return (
    <>
      <Helmet title="PRF Statistics Tool" />

      <PageWrapper>
        <header>
          <Typography variant="h3" component="h1" gutterBottom>
            PRF Statistics Tool
          </Typography>
          <Divider my={4} />
        </header>

        <Main>
          <Section>
            <Typography variant="h5" component="h2" gutterBottom>
              About this Page
            </Typography>

            <Typography variant="body1" gutterBottom>
              This page embeds an external analytics tool that displays
              statistics for water quality monitoring points associated with{" "}
              <strong>Pollution Reduction Facilities (PRFs)</strong>. The tool
              is built and hosted outside of this application via a Shiny
              dashboard, and it may not meet all accessibility (ADA) standards.
            </Typography>

            <Typography variant="body1" gutterBottom>
              The dashboard allows users to:
            </Typography>

            <ul>
              <li>
                View <strong>annual concentrations</strong> of selected water
                quality parameters at individual monitoring points.
              </li>
              <li>
                Compare <strong>“before” and “after”</strong> concentrations
                across custom date ranges to evaluate the effectiveness of PRF
                installations or maintenance efforts.
              </li>
              <li>
                Explore <strong>summary statistics</strong> and statistical test
                results, including a Mann-Whitney U test to assess significant
                differences over time.
              </li>
              <li>
                Visualize <strong>monitoring site locations</strong> and flow
                pathways using an interactive map.
              </li>
            </ul>

            <Typography variant="body1" gutterBottom>
              All site selections, date filters, flow conditions, and seasonal
              settings dynamically update the graphs and tables to support
              customized analysis.
            </Typography>

            <Typography variant="body1">
              This tool supports long-term performance tracking of water quality
              mitigation infrastructure and helps inform adaptive management
              strategies.
            </Typography>
          </Section>

          <SectionGrowWrapper>
            <Box display="flex" justifyContent="flex-end" mb={1}>
              <Button
                variant="outlined"
                size="small"
                endIcon={<OpenInNewIcon />}
                onClick={() => setFullscreenOpen(true)}
              >
                View Fullscreen
              </Button>
            </Box>

            {isLoading && (
              <Box
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <CircularProgress />
              </Box>
            )}

            <StyledIframe
              src="https://lrewater.shinyapps.io/CCBWQA_PRFStatsTool/"
              title="PRF Statistics Tool"
              style={{ display: isLoading ? "none" : "block" }}
              onLoad={() => setIsLoading(false)}
            />
          </SectionGrowWrapper>
        </Main>
      </PageWrapper>

      <Dialog
        fullScreen
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        TransitionComponent={Transition}
      >
        <AppBar position="relative">
          <Toolbar>
            <Typography variant="h6" style={{ flex: 1 }}>
              PRF Statistics Tool
            </Typography>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => setFullscreenOpen(false)}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
        </AppBar>

        <StyledIframe
          src="https://lrewater.shinyapps.io/CCBWQA_PRFStatsTool/"
          title="PRF Statistics Tool Fullscreen"
        />
      </Dialog>
    </>
  );
};

export default PrfStatisticsTool;
